"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { isOwnProfileRoute } from "@/helpers/ProfileHelpers";
import useCapacitor from "@/hooks/useCapacitor";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import UserPageTab from "./UserPageTab";
import { DEFAULT_USER_PAGE_TAB, USER_PAGE_TABS, USER_PAGE_TAB_IDS, type UserPageTabKey, type UserPageVisibilityContext, getUserPageTabByRoute } from "./userTabs.config";
import { shouldHideSubscriptions } from "./userPageVisibility";
import { shouldDelayUserPageBrainRedirect } from "./userPageBrainAccess";
const DEFAULT_TAB = DEFAULT_USER_PAGE_TAB;
const subscribeToClientRender = () => () => undefined;
const getClientRenderSnapshot = () => true;
const getServerRenderSnapshot = () => false;
const getVisibilityContext = ({
  showWaves,
  capacitorIsIos,
  country,
  isOwnProfile
}: {
  readonly showWaves: boolean;
  readonly capacitorIsIos: boolean;
  readonly country: string | null | undefined;
  readonly isOwnProfile: boolean;
}): UserPageVisibilityContext => {
  return {
    showWaves,
    hideSubscriptions: shouldHideSubscriptions({
      capacitorIsIos,
      country
    }),
    isOwnProfile
  };
};
const resolveTabFromPath = (pathname: string): UserPageTabKey => {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] ?? "";
  const match = getUserPageTabByRoute(routeSegment);
  return match?.id ?? DEFAULT_TAB;
};
export default function UserPageTabs() {
  const $ = _c(69);
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  let t0;
  if ($[0] !== params.user) {
    t0 = params.user?.toString() ?? "";
    $[0] = params.user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleOrWallet = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams.toString();
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchString = t1;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves,
    connectedProfile,
    fetchingProfile
  } = useAuth();
  const {
    address,
    connectionState
  } = useSeizeConnectContext();
  let t2;
  if ($[4] !== connectedProfile || $[5] !== handleOrWallet) {
    t2 = isOwnProfileRoute({
      connectedProfile,
      handleOrWallet
    });
    $[4] = connectedProfile;
    $[5] = handleOrWallet;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const isOwnProfile = t2;
  let t3;
  if ($[7] !== capacitor.isIos || $[8] !== country || $[9] !== isOwnProfile || $[10] !== showWaves) {
    t3 = getVisibilityContext({
      showWaves,
      capacitorIsIos: capacitor.isIos,
      country,
      isOwnProfile
    });
    $[7] = capacitor.isIos;
    $[8] = country;
    $[9] = isOwnProfile;
    $[10] = showWaves;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const visibilityContext = t3;
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const isClientHydrated = useSyncExternalStore(subscribeToClientRender, getClientRenderSnapshot, getServerRenderSnapshot);
  let t4;
  if ($[12] !== pathname) {
    t4 = resolveTabFromPath(pathname);
    $[12] = pathname;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const resolvedTabFromPath = t4;
  const preserveProxyTabWhileOwnershipLoads = fetchingProfile && !connectedProfile && resolvedTabFromPath === USER_PAGE_TAB_IDS.PROXY;
  let t5;
  if ($[14] !== address || $[15] !== connectedProfile || $[16] !== connectionState || $[17] !== fetchingProfile || $[18] !== isClientHydrated || $[19] !== resolvedTabFromPath) {
    t5 = resolvedTabFromPath === USER_PAGE_TAB_IDS.BRAIN && shouldDelayUserPageBrainRedirect({
      address,
      connectedProfile,
      connectionState,
      fetchingProfile,
      isClientHydrated
    });
    $[14] = address;
    $[15] = connectedProfile;
    $[16] = connectionState;
    $[17] = fetchingProfile;
    $[18] = isClientHydrated;
    $[19] = resolvedTabFromPath;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const shouldSuppressBrainRedirect = t5;
  const preserveBrainTabWhileAccessLoads = shouldSuppressBrainRedirect;
  let t6;
  if ($[21] !== preserveBrainTabWhileAccessLoads || $[22] !== preserveProxyTabWhileOwnershipLoads || $[23] !== visibilityContext) {
    t6 = USER_PAGE_TABS.filter(tab => {
      if (preserveProxyTabWhileOwnershipLoads && tab.id === USER_PAGE_TAB_IDS.PROXY) {
        return true;
      }
      if (preserveBrainTabWhileAccessLoads && tab.id === USER_PAGE_TAB_IDS.BRAIN) {
        return true;
      }
      return tab.isVisible ? tab.isVisible(visibilityContext) : true;
    });
    $[21] = preserveBrainTabWhileAccessLoads;
    $[22] = preserveProxyTabWhileOwnershipLoads;
    $[23] = visibilityContext;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const visibleTabs = t6;
  let t7;
  if ($[25] !== resolvedTabFromPath || $[26] !== visibleTabs) {
    let t8;
    if ($[28] !== resolvedTabFromPath) {
      t8 = tab_0 => tab_0.id === resolvedTabFromPath;
      $[28] = resolvedTabFromPath;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    t7 = visibleTabs.some(t8);
    $[25] = resolvedTabFromPath;
    $[26] = visibleTabs;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const resolvedTabIsVisible = t7;
  let t8;
  bb0: {
    if (resolvedTabIsVisible) {
      t8 = resolvedTabFromPath;
      break bb0;
    }
    const firstVisibleTab = visibleTabs[0]?.id;
    t8 = firstVisibleTab ?? DEFAULT_TAB;
  }
  const activeTab = t8;
  let t9;
  if ($[30] !== handleOrWallet || $[31] !== pathname || $[32] !== resolvedTabIsVisible || $[33] !== router || $[34] !== searchString || $[35] !== shouldSuppressBrainRedirect || $[36] !== visibleTabs[0] || $[37] !== visibleTabs.length) {
    t9 = () => {
      if (!visibleTabs.length || resolvedTabIsVisible || !handleOrWallet || shouldSuppressBrainRedirect) {
        return;
      }
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab) {
        return;
      }
      const fallbackPath = fallbackTab.route ? `/${handleOrWallet}/${fallbackTab.route}` : `/${handleOrWallet}`;
      if (fallbackPath === pathname) {
        return;
      }
      const nextUrl = searchString ? `${fallbackPath}?${searchString}` : fallbackPath;
      router.replace(nextUrl);
    };
    $[30] = handleOrWallet;
    $[31] = pathname;
    $[32] = resolvedTabIsVisible;
    $[33] = router;
    $[34] = searchString;
    $[35] = shouldSuppressBrainRedirect;
    $[36] = visibleTabs[0];
    $[37] = visibleTabs.length;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== handleOrWallet || $[40] !== pathname || $[41] !== resolvedTabIsVisible || $[42] !== router || $[43] !== searchString || $[44] !== shouldSuppressBrainRedirect || $[45] !== visibleTabs) {
    t10 = [handleOrWallet, pathname, resolvedTabIsVisible, router, searchString, shouldSuppressBrainRedirect, visibleTabs];
    $[39] = handleOrWallet;
    $[40] = pathname;
    $[41] = resolvedTabIsVisible;
    $[42] = router;
    $[43] = searchString;
    $[44] = shouldSuppressBrainRedirect;
    $[45] = visibleTabs;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  useEffect(t9, t10);
  let t11;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  const checkScroll = t11;
  let t12;
  let t13;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(container_0);
        if (contentContainer) {
          resizeObserver.observe(contentContainer);
        }
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver?.disconnect();
      };
    };
    t13 = [checkScroll];
    $[48] = t12;
    $[49] = t13;
  } else {
    t12 = $[48];
    t13 = $[49];
  }
  useEffect(t12, t13);
  let t14;
  if ($[50] !== visibleTabs.length) {
    t14 = () => {
      if (!visibleTabs.length) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[50] = visibleTabs.length;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== visibleTabs) {
    t15 = [checkScroll, visibleTabs];
    $[52] = visibleTabs;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  useEffect(t14, t15);
  let t16;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      container_1.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const scrollLeft_0 = t16;
  let t17;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const scrollRight = t17;
  let t18;
  if ($[56] !== activeTab || $[57] !== visibleTabs) {
    t18 = visibleTabs.map(tabConfig => <UserPageTab key={tabConfig.id} tab={tabConfig} activeTabId={activeTab} parentRef={scrollContainerRef} />);
    $[56] = activeTab;
    $[57] = visibleTabs;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t18) {
    t19 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4" aria-label="Tabs">{t18}</div></div>;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== canScrollLeft) {
    t20 = canScrollLeft && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-group tw-absolute tw-left-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-start tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[61] = canScrollLeft;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== canScrollRight) {
    t21 = canScrollRight && <><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-z-10 tw-w-24 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-group tw-absolute tw-right-0 tw-top-1/2 tw-z-20 tw-inline-flex tw-h-10 tw-w-10 tw--translate-y-1/2 tw-items-center tw-justify-end tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 tw-transition tw-duration-300 tw-ease-out group-hover:tw-text-iron-300" /></button></>;
    $[63] = canScrollRight;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t19 || $[66] !== t20 || $[67] !== t21) {
    t22 = <div className="tw-relative tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700">{t19}{t20}{t21}</div>;
    $[65] = t19;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  return t22;
}